/**
 * Sets table - Sortable table displaying MTG sets
 * Uses TanStack Table for sorting functionality
 */
import {
  createColumnHelper,
  flexRender,
  getCoreRowModel,
  getSortedRowModel,
  useReactTable,
  type SortingState,
} from '@tanstack/react-table';
import clsx from 'clsx';
import { format, isFuture, parseISO } from 'date-fns';
import { useMemo, useState, type ReactElement } from 'react';
import { IoCaretDown, IoCaretUp, IoLibraryOutline, IoOpenOutline, IoTimeOutline } from 'react-icons/io5';
import { Link, useNavigate } from 'react-router-dom';

import SetIcon from '@/components/SetIcon/SetIcon';
import { SET_TYPE_LABELS } from '@/lib/cardUtils';
import type { ScryfallSet } from '@/types/card';

export interface SetsTableProps {
  sets: ScryfallSet[];
}

/** Check if a set is not yet released (release date is in the future or missing) */
function isUnreleased(set: ScryfallSet): boolean {
  if (!set.released_at) return true;
  try {
    return isFuture(parseISO(set.released_at));
  } catch {
    return false;
  }
}

// Badge colors for set types
const SET_TYPE_COLORS: Record<string, string> = {
  expansion: 'bg-blue-500/20 text-blue-300',
  core: 'bg-amber-500/20 text-amber-300',
  masters: 'bg-purple-500/20 text-purple-300',
  masterpiece: 'bg-yellow-500/20 text-yellow-300',
  draft_innovation: 'bg-teal-500/20 text-teal-300',
  commander: 'bg-green-500/20 text-green-300',
  starter: 'bg-cyan-500/20 text-cyan-300',
  duel_deck: 'bg-orange-500/20 text-orange-300',
  from_the_vault: 'bg-pink-500/20 text-pink-300',
  premium_deck: 'bg-rose-500/20 text-rose-300',
  spellbook: 'bg-indigo-500/20 text-indigo-300',
  arsenal: 'bg-red-500/20 text-red-300',
  box: 'bg-slate-500/20 text-slate-300',
  promo: 'bg-fuchsia-500/20 text-fuchsia-300',
  token: 'bg-gray-500/20 text-gray-300',
  funny: 'bg-lime-500/20 text-lime-300',
  memorabilia: 'bg-stone-500/20 text-stone-300',
  alchemy: 'bg-violet-500/20 text-violet-300',
  minigame: 'bg-emerald-500/20 text-emerald-300',
};

const columnHelper = createColumnHelper<ScryfallSet>();

export default function SetsTable({ sets }: SetsTableProps): ReactElement {
  const [sorting, setSorting] = useState<SortingState>([{ id: 'released_at', desc: true }]);
  const navigate = useNavigate();

  const columns = useMemo(
    () => [
      // Icon column
      columnHelper.display({
        id: 'icon',
        header: '',
        size: 48,
        cell: ({ row }) => (
          <SetIcon setCode={row.original.code} scryfallIconUri={row.original.icon_svg_uri} size="lg" className="text-2xl" />
        ),
      }),

      // Name column
      columnHelper.accessor('name', {
        header: 'Name',
        cell: ({ getValue, row }) => {
          const upcoming = isUnreleased(row.original);
          return (
            <div className="flex items-center gap-2">
              <span className={clsx('font-medium', upcoming ? 'text-silver italic' : 'text-pearl')}>{getValue()}</span>
              {upcoming && (
                <span className="inline-flex items-center gap-1 rounded-full bg-amber-500/20 px-2 py-0.5 text-xs font-medium text-amber-300">
                  <IoTimeOutline className="h-3 w-3" />
                  Upcoming
                </span>
              )}
            </div>
          );
        },
      }),

      // Code column
      columnHelper.accessor('code', {
        header: 'Code',
        size: 80,
        cell: ({ getValue }) => <span className="text-silver font-mono text-sm uppercase">{getValue()}</span>,
      }),

      // Release date column
      columnHelper.accessor('released_at', {
        header: 'Released',
        size: 100,
        sortingFn: (rowA, rowB) => {
          const a = rowA.original.released_at;
          const b = rowB.original.released_at;
          if (!a && !b) return 0;
          if (!a) return 1;
          if (!b) return -1;
          return a.localeCompare(b);
        },
        cell: ({ getValue }) => {
          const value = getValue();
          if (!value) return <span className="text-ash">—</span>;
          try {
            const date = parseISO(value);
            return <span className="text-silver">{format(date, 'MMM yyyy')}</span>;
          } catch {
            return <span className="text-ash">—</span>;
          }
        },
      }),

      // Card count column
      columnHelper.accessor('card_count', {
        header: 'Cards',
        size: 80,
        cell: ({ getValue }) => <span className="text-silver text-right font-mono">{getValue().toLocaleString()}</span>,
      }),

      // Set type column
      columnHelper.accessor('set_type', {
        header: 'Type',
        size: 140,
        cell: ({ getValue }) => {
          const type = getValue();
          const label = SET_TYPE_LABELS[type] || type;
          const colorClass = SET_TYPE_COLORS[type] || 'bg-slate text-silver';
          return <span className={clsx('rounded-full px-2 py-0.5 text-xs font-medium', colorClass)}>{label}</span>;
        },
      }),

      // Actions column
      columnHelper.display({
        id: 'actions',
        header: '',
        size: 80,
        cell: ({ row }) => (
          <div className="flex items-center justify-end gap-1" onClick={(e) => e.stopPropagation()}>
            <Link
              to={`/?sets=${row.original.code}`}
              className="text-silver hover:text-pearl hover:bg-slate rounded p-1.5 transition-colors"
              title="View in Collection"
            >
              <IoLibraryOutline className="h-4 w-4" />
            </Link>
            <a
              href={row.original.scryfall_uri}
              target="_blank"
              rel="noopener noreferrer"
              className="text-silver hover:text-pearl hover:bg-slate rounded p-1.5 transition-colors"
              title="View on Scryfall"
            >
              <IoOpenOutline className="h-4 w-4" />
            </a>
          </div>
        ),
      }),
    ],
    [],
  );

  const table = useReactTable({
    data: sets,
    columns,
    state: { sorting },
    onSortingChange: setSorting,
    getCoreRowModel: getCoreRowModel(),
    getSortedRowModel: getSortedRowModel(),
  });

  return (
    <div className="border-storm overflow-x-auto rounded-lg border">
      <table className="w-full">
        <thead className="bg-slate">
          {table.getHeaderGroups().map((headerGroup) => (
            <tr key={headerGroup.id}>
              {headerGroup.headers.map((header) => {
                const canSort = header.column.getCanSort();
                const sortDir = header.column.getIsSorted();

                return (
                  <th
                    key={header.id}
                    className={clsx(
                      'text-ash px-4 py-3 text-left text-xs font-medium tracking-wider uppercase',
                      canSort && 'hover:text-pearl cursor-pointer select-none',
                    )}
                    style={{ width: header.getSize() !== 150 ? header.getSize() : undefined }}
                    onClick={canSort ? header.column.getToggleSortingHandler() : undefined}
                  >
                    <div className="flex items-center gap-1">
                      {flexRender(header.column.columnDef.header, header.getContext())}
                      {sortDir === 'asc' && <IoCaretUp className="h-3 w-3" />}
                      {sortDir === 'desc' && <IoCaretDown className="h-3 w-3" />}
                    </div>
                  </th>
                );
              })}
            </tr>
          ))}
        </thead>
        <tbody className="divide-storm divide-y">
          {table.getRowModel().rows.map((row) => {
            const upcoming = isUnreleased(row.original);
            return (
              <tr
                key={row.id}
                className={clsx(
                  'cursor-pointer transition-colors',
                  upcoming ? 'bg-amber-500/5 hover:bg-amber-500/10' : 'hover:bg-slate/50',
                )}
                onClick={() => navigate(`/sets/${row.original.code}`)}
              >
                {row.getVisibleCells().map((cell) => (
                  <td key={cell.id} className="px-4 py-3 text-sm">
                    {flexRender(cell.column.columnDef.cell, cell.getContext())}
                  </td>
                ))}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
